import { RefreshCw, Play, Square, RotateCw, FileText } from 'lucide-react'
import { useGetAdminStatusQuery, useStartServiceMutation, useStopServiceMutation } from '@/store/api'

const SERVICES = [
  { name: 'preprocess', port: 8090, gpu: null },
  { name: 'jina-clip', port: 8100, gpu: 0 },
  { name: 'classifier', port: 8101, gpu: 0 },
  { name: 'qwen-vl', port: 8102, gpu: 1 },
  { name: 'qwen-llm', port: 8103, gpu: 0 },
  { name: 'colpali', port: 8104, gpu: 1 },
  { name: 'insightface', port: 8105, gpu: 1 },
]

function AdminPage() {
  const { data: status, isLoading, refetch } = useGetAdminStatusQuery()
  const [startService] = useStartServiceMutation()
  const [stopService] = useStopServiceMutation()

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Admin: Service Control</h1>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-1.5 hover:bg-secondary rounded-md text-sm"
        >
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <button className="flex flex-col items-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-green-500/50 transition-colors">
          <Play size={24} className="text-green-500" />
          <span className="font-medium">Start All</span>
        </button>
        <button className="flex flex-col items-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-red-500/50 transition-colors">
          <Square size={24} className="text-red-500" />
          <span className="font-medium">Stop All</span>
        </button>
        <button className="flex flex-col items-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-yellow-500/50 transition-colors">
          <RotateCw size={24} className="text-yellow-500" />
          <span className="font-medium">Restart All</span>
        </button>
      </div>

      <div className="border-t border-border my-6" />

      {/* Services Table */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Services</h2>
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-secondary">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium">Service</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium">GPU</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Port</th>
                <th className="text-right px-4 py-3 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {SERVICES.map((service) => {
                const svcStatus = status?.services.find(s => s.name === service.name)
                const isRunning = svcStatus?.status === 'running'

                return (
                  <tr key={service.name} className="hover:bg-secondary/50">
                    <td className="px-4 py-3 font-medium">{service.name}</td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2">
                        <span className={`status-dot ${isRunning ? 'status-running' : 'status-stopped'}`} />
                        {isRunning ? 'Running' : 'Stopped'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {service.gpu !== null ? `GPU${service.gpu}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{service.port}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {isRunning ? (
                          <button
                            onClick={() => stopService(service.name)}
                            className="px-2 py-1 text-sm hover:bg-destructive/20 text-destructive rounded"
                          >
                            Stop
                          </button>
                        ) : (
                          <button
                            onClick={() => startService(service.name)}
                            className="px-2 py-1 text-sm hover:bg-green-500/20 text-green-500 rounded"
                          >
                            Start
                          </button>
                        )}
                        <button className="px-2 py-1 text-sm hover:bg-secondary rounded">
                          <FileText size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="border-t border-border my-6" />

      {/* GPU Status */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-4">GPU Status</h2>
        <div className="bg-card border border-border rounded-lg p-4 space-y-4">
          {[0, 1].map((gpu) => {
            const gpuStatus = status?.gpus.find(g => g.index === gpu)
            const usedPct = gpuStatus
              ? (gpuStatus.memory_used_mb / gpuStatus.memory_total_mb) * 100
              : 0

            return (
              <div key={gpu}>
                <div className="flex justify-between text-sm mb-1">
                  <span>GPU {gpu} (RTX 5090)</span>
                  <span className="text-muted-foreground">
                    {gpuStatus
                      ? `${(gpuStatus.memory_used_mb / 1024).toFixed(1)} GB / ${(gpuStatus.memory_total_mb / 1024).toFixed(0)} GB`
                      : 'Offline'}
                  </span>
                </div>
                <div className="h-3 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${usedPct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* Processing Queue */}
      <section>
        <h2 className="text-lg font-semibold mb-4">Processing Queue</h2>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-around text-center">
            <div>
              <div className="text-2xl font-bold">{status?.queue.pending ?? 0}</div>
              <div className="text-sm text-muted-foreground">Pending</div>
            </div>
            <div className="h-8 border-l border-border" />
            <div>
              <div className="text-2xl font-bold">{status?.queue.in_progress ?? 0}</div>
              <div className="text-sm text-muted-foreground">In Progress</div>
            </div>
            <div className="h-8 border-l border-border" />
            <div>
              <div className="text-2xl font-bold text-destructive">{status?.queue.failed ?? 0}</div>
              <div className="text-sm text-muted-foreground">Failed</div>
            </div>
            <div className="h-8 border-l border-border" />
            <button className="px-3 py-1.5 text-sm hover:bg-secondary rounded-md">
              View DLQ
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default AdminPage
